/**
 * Purple Heart Gallery & Framing — Level 1 artwork image protection
 * Added 08 Sep 2026.
 *
 * Blocks the browser's normal image context menu and drag-to-desktop behaviour
 * for product/artwork images only. It intentionally does not disable right-click
 * elsewhere on the site and does not interfere with normal product image clicks,
 * zoom controls, sliders, navigation, forms or checkout.
 *
 * This is a deterrent, not DRM. Publicly rendered images can still be obtained
 * by determined users through screenshots or developer tools.
 */
(() => {
  const protectedImageSelector = [
    '.product-card-wrapper img',
    '.card-wrapper.product-card-wrapper img',
    '.product__media img',
    '.product-media-container img',
    '.product-media-modal img',
    '.quick-add__product-media',
    '.quick-add__content-info__media img',
    '.predictive-search__image',
    '.cart-item__image',
  ].join(',');

  const isProtectedImage = (node) =>
    node instanceof Element && node.matches(protectedImageSelector);

  const protectImage = (img) => {
    if (!(img instanceof HTMLImageElement) || !isProtectedImage(img)) return;
    img.setAttribute('draggable', 'false');
    img.setAttribute('data-purple-heart-image-protected', 'true');
  };

  const protectImagesWithin = (root) => {
    if (!(root instanceof Element || root instanceof Document)) return;
    if (root instanceof HTMLImageElement) protectImage(root);
    root.querySelectorAll?.(protectedImageSelector).forEach(protectImage);
  };

  // Protect images already present at load time.
  const init = () => protectImagesWithin(document);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // Dawn can inject product/search/cart imagery after initial load.
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node instanceof Element) protectImagesWithin(node);
      }
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });

  // Removes Save Image / Copy Image / Open Image from the normal context menu
  // only when the click is directly on protected product imagery.
  document.addEventListener(
    'contextmenu',
    (event) => {
      if (!isProtectedImage(event.target)) return;
      event.preventDefault();
      event.stopPropagation();
    },
    { capture: true }
  );

  // Prevent drag-to-desktop / drag-to-new-tab behaviour.
  document.addEventListener(
    'dragstart',
    (event) => {
      if (!isProtectedImage(event.target)) return;
      event.preventDefault();
      event.stopPropagation();
    },
    { capture: true }
  );

  // Helps suppress selection/copy interactions on image elements without
  // disabling text selection elsewhere on the storefront.
  document.addEventListener(
    'selectstart',
    (event) => {
      if (!isProtectedImage(event.target)) return;
      event.preventDefault();
    },
    { capture: true }
  );
})();
